import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useTranslation } from 'react-i18next';

interface AppleLoginButtonProps {
  isRecruiter?: boolean;
  disabled?: boolean;
  className?: string;
}

const AppleLoginButton: React.FC<AppleLoginButtonProps> = ({ 
  isRecruiter = false, 
  disabled = false,
  className = ''
}) => {
  const { t } = useTranslation('common');
  const [loading, setLoading] = useState(false);

  const handleAppleLogin = async () => {
    setLoading(true);
    
    try {
      // Store recruiter flag in sessionStorage for after OAuth callback
      if (isRecruiter) {
        sessionStorage.setItem('google_signup_recruiter', 'true');
      } else {
        sessionStorage.removeItem('google_signup_recruiter');
      }
      
      // Store return URL for redirect after login (unless recruiter signup)
      if (!isRecruiter) {
        const urlParams = new URLSearchParams(window.location.search);
        const returnParam = urlParams.get('return');
        
        const authPages = ['/login', '/signup', '/signup-choice', '/recruiter-signup', '/email-confirmation'];
        const isAuthPage = authPages.some(page => window.location.pathname.startsWith(page));
        
        if (returnParam) {
          sessionStorage.setItem('google_return_url', returnParam);
        } else if (!isAuthPage) {
          sessionStorage.setItem('google_return_url', window.location.pathname + window.location.search);
        }
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        console.error('Apple login error:', error);
        setLoading(false);
      }
    } catch (err) {
      console.error('Apple login error:', err);
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleAppleLogin}
      disabled={disabled || loading}
      className={`w-full flex items-center justify-center gap-3 py-2.5 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${className}`}
    >
      {/* Apple Logo SVG */}
      <svg className="w-5 h-5" viewBox="1.5 2 21 21" fill="currentColor">
        <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
      </svg>
      {loading ? t('appleLogin.loading', 'Connecting...') : t('appleLogin.button', 'Continue with Apple')}
    </button>
  );
};

export default AppleLoginButton;
